import type { CropDef } from '../data/crops';

/** A crop planted on a tile. Serializable; lives in GameState.crops. */
export interface PlantedCrop {
  cropId: string;
  x: number;
  y: number;
  plantedDay: number;
  /** Days of *watered* growth accumulated. */
  growth: number;
  /** Watered today? Reset each day rollover. */
  watered: boolean;
}

export function newPlantedCrop(cropId: string, x: number, y: number, day: number): PlantedCrop {
  return { cropId, x, y, plantedDay: day, growth: 0, watered: false };
}

/** Current growth-stage frame index in [0, def.stages-1]. */
export function growthStage(crop: PlantedCrop, def: CropDef): number {
  if (def.growthDays <= 0) return def.stages - 1;
  const frac = crop.growth / def.growthDays;
  const stage = Math.floor(frac * (def.stages - 1));
  return Math.max(0, Math.min(def.stages - 1, stage));
}

export function isHarvestable(crop: PlantedCrop, def: CropDef): boolean {
  return crop.growth >= def.growthDays;
}

/** One day rollover: a watered crop grows a day; the watered flag always resets. Pure. */
export function tickCropDay(crop: PlantedCrop, def: CropDef, growthAmount = 1): PlantedCrop {
  if (!crop.watered) return { ...crop, watered: false };
  const growth = Math.min(def.growthDays, crop.growth + growthAmount);
  return { ...crop, growth, watered: false };
}

export function waterCrop(crop: PlantedCrop): PlantedCrop {
  return { ...crop, watered: true };
}

export interface HarvestResult {
  /** Crop after harvest: regrow crops persist (reset); single-use return null. */
  crop: PlantedCrop | null;
  produceItemId: string;
  qty: number;
}

/** Harvest if ready. Returns null when not harvestable. Pure. */
export function harvestCrop(crop: PlantedCrop, def: CropDef): HarvestResult | null {
  if (!isHarvestable(crop, def)) return null;
  if (def.regrowDays != null) {
    const regrown: PlantedCrop = {
      ...crop,
      growth: Math.max(0, def.growthDays - def.regrowDays),
      watered: false,
    };
    return { crop: regrown, produceItemId: def.produceItemId, qty: def.yield };
  }
  return { crop: null, produceItemId: def.produceItemId, qty: def.yield };
}
